import React from "react";
import {
  fetchTenantLocations,
  createTenantLocation,
  deleteTenantLocation,
  fetchLocationSuggestions,
} from "../../../api/backend";

const SUGGEST_DEBOUNCE_MS = 300;
const SUGGEST_MIN_CHARS = 2;

const errorText = (error, fallback) => error?.response?.data?.error || error?.message || fallback;

/** Saved tenant locations plus OpenStreetMap suggestions for the session form; keeps `form.location` in step with add/remove. */
export default function useSessionLocations({ form, setForm }) {
  const [locations, setLocations] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [query, setQuery] = React.useState("");
  const [suggestions, setSuggestions] = React.useState([]);
  const [suggesting, setSuggesting] = React.useState(false);
  const timer = React.useRef(null);

  React.useEffect(() => {
    let live = true;
    fetchTenantLocations()
      .then((list) => live && setLocations(Array.isArray(list) ? list : []))
      .catch(() => live && setError("Unable to load tenant locations."));
    return () => {
      live = false;
      clearTimeout(timer.current);
    };
  }, []);

  const select = (name) => setForm((prev) => ({ ...prev, location: name }));

  const add = async () => {
    const name = query.trim();
    if (!name) return;
    setBusy(true);
    setError(null);
    try {
      const created = await createTenantLocation({ name });
      setLocations((prev) => [...prev, created].sort((a, b) => String(a.name).localeCompare(String(b.name))));
      select(created.name);
      setQuery("");
    } catch (e) {
      setError(errorText(e, "Unable to add location."));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    setBusy(true);
    setError(null);
    const removed = locations.find((loc) => loc.id === id);
    try {
      await deleteTenantLocation(id);
      setLocations((prev) => prev.filter((loc) => loc.id !== id));
      if (removed && removed.name === form.location) select("");
    } catch (e) {
      setError(errorText(e, "Unable to remove location."));
    } finally {
      setBusy(false);
    }
  };

  const onInput = (value) => {
    setQuery(value);
    clearTimeout(timer.current);
    if (value.length < SUGGEST_MIN_CHARS) {
      setSuggestions([]);
      setSuggesting(false);
      return;
    }
    setSuggesting(true);
    timer.current = setTimeout(async () => {
      try {
        const found = await fetchLocationSuggestions(value);
        setSuggestions(Array.isArray(found) ? found : []);
      } catch (e) {
        console.error("Error fetching location suggestions:", e);
        setSuggestions([]);
      } finally {
        setSuggesting(false);
      }
    }, SUGGEST_DEBOUNCE_MS);
  };

  const pick = (suggestion) => {
    if (!suggestion) return;
    setQuery(suggestion.display_name || "");
    setSuggestions([]);
  };

  return { locations, busy, error, query, suggestions, suggesting, select, add, remove, onInput, pick };
}
